import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Chart from 'react-apexcharts';
import { ShieldAlert, CreditCard, Users, Layers, TrendingUp, X } from 'lucide-react';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.css';
import api from '../../utils/api';

const SCENARIO_LABELS = {
  'customer-to-many-inmates': '1 customer → many inmates',
  'many-customers-to-inmate': 'Many customers → 1 inmate',
  'multi-state': 'Multi-state',
  'shared-card': 'Shared card',
  other: 'Other / manual',
};

const RISK_BADGE = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
};

const RISK_ROW = {
  critical: 'border-l-4 border-red-500',
  high: 'border-l-4 border-orange-400',
  medium: 'border-l-4 border-yellow-400',
};

const CHART_COLORS = ['#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', '#94A3B8', '#10B981'];

const KpiCard = ({ label, value, tone = 'default', icon: Icon }) => {
  const tones = {
    default: 'text-gray-900',
    info: 'text-blue-600',
    danger: 'text-red-600',
    warning: 'text-orange-600',
    success: 'text-green-600',
  };
  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${tones[tone] || tones.default}`}>{value}</p>
        </div>
        {Icon && <Icon className={`w-6 h-6 ${tones[tone] || tones.default} opacity-70`} />}
      </div>
    </div>
  );
};

// Format a Date as a local YYYY-MM-DD string.
const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Today's date as a local YYYY-MM-DD string (for the default date filter).
const todayStr = () => fmtDate(new Date());

// Combined per-flag detail, e.g. "3 users · 5 inmates · 5 orders".
// Parts whose count is 0 are omitted; empty overall renders as "—".
const flagDetail = (f) => {
  const parts = [];
  if (f.customerCount) parts.push(`${f.customerCount} users`);
  if (f.inmateCount) parts.push(`${f.inmateCount} inmates`);
  if (f.orderCount) parts.push(`${f.orderCount} orders`);
  return parts.length ? parts.join(' · ') : '—';
};

const DepositFlagReview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('priority'); // priority | overlap | shared-card | critical
  const [riskFilter, setRiskFilter] = useState('all');
  const [selectedFlagId, setSelectedFlagId] = useState(null);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [searchCustomerId, setSearchCustomerId] = useState('');
  const [searchInmateId, setSearchInmateId] = useState('');
  const [searchOrderId, setSearchOrderId] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        const res = await api.get('/reports/deposit-flag-review', { params });
        setData(res.data.data);
      } catch (err) {
        console.error('Error fetching deposit flag review:', err);
        setError('Unable to load flag review analytics.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate]);

  const summary = data?.summary;
  const overlapCases = useMemo(() => data?.inmateOverlapCases || [], [data]);

  const pie = useMemo(() => {
    // Prefer the dynamic trigger-type breakdown (grouped by the flag's actual
    // `source`); fall back to the scenario taxonomy for older API responses.
    const counts = summary?.triggerTypeCounts || summary?.scenarioCounts || {};
    const entries = Object.entries(counts)
      .filter(([, v]) => Number(v) > 0)
      .sort((a, b) => b[1] - a[1]);
    return { labels: entries.map(([k]) => k), series: entries.map(([, v]) => Number(v)) };
  }, [summary]);

  const riskBar = useMemo(() => {
    const rc = summary?.riskCounts || {};
    return [rc.critical || 0, rc.high || 0, rc.medium || 0];
  }, [summary]);

  // Customer spread comes from cms_case_suspects (inmates linked per customer).
  const customerSpread = useMemo(() => data?.customerSpread || [], [data]);

  const topOverlap = useMemo(
    () => [...overlapCases].sort((a, b) => b.flagCount - a.flagCount).slice(0, 10),
    [overlapCases]
  );

  // Inmate overlap chart: customers per inmate (flag_type many-customers-to-inmate).
  const inmateOverlapChart = useMemo(() => (data?.inmateOverlapChart || []).slice(0, 10), [data]);

  // Review queue is one row per flag, sourced from cms_flag_entities.
  const reviewQueue = useMemo(() => data?.reviewQueue || [], [data]);
  const isSharedCardType = (t) => t === 'shared-card' || t === 'shared-cards';

  // Filter the queue by customer / inmate / order id (substring match against
  // the row's id lists). Empty inputs are ignored; multiple inputs are ANDed.
  const filteredQueue = useMemo(() => {
    const c = searchCustomerId.trim();
    const i = searchInmateId.trim();
    const o = searchOrderId.trim();
    if (!c && !i && !o) return reviewQueue;
    const has = (arr, term) => !term || (arr || []).some((v) => String(v).includes(term));
    return reviewQueue.filter(
      (f) => has(f.customerIds, c) && has(f.inmateIds, i) && has(f.orderIds, o)
    );
  }, [reviewQueue, searchCustomerId, searchInmateId, searchOrderId]);

  const sharedCardRows = useMemo(() => filteredQueue.filter((f) => isSharedCardType(f.flagType)), [filteredQueue]);
  const criticalRows = useMemo(() => filteredQueue.filter((f) => f.risk === 'critical'), [filteredQueue]);
  const priorityRows = useMemo(
    () => (riskFilter === 'all' ? filteredQueue : filteredQueue.filter((f) => f.risk === riskFilter)),
    [filteredQueue, riskFilter]
  );

  const activeFlagRows =
    viewMode === 'shared-card' ? sharedCardRows : viewMode === 'critical' ? criticalRows : priorityRows;

  const selectedFlag = reviewQueue.find((f) => f.id === selectedFlagId);

  // Pagination for the review queue (10 rows per page). Applies to the overlap
  // table in overlap mode, otherwise to the flag rows.
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const currentRows = viewMode === 'overlap' ? overlapCases : activeFlagRows;
  const totalPages = Math.max(1, Math.ceil(currentRows.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageStart = (pageSafe - 1) * PAGE_SIZE;
  const pagedOverlap = viewMode === 'overlap' ? overlapCases.slice(pageStart, pageStart + PAGE_SIZE) : [];
  const pagedFlags = viewMode !== 'overlap' ? activeFlagRows.slice(pageStart, pageStart + PAGE_SIZE) : [];

  // Reset to the first page whenever the view, filter, or date range changes.
  useEffect(() => {
    setPage(1);
  }, [viewMode, riskFilter, startDate, endDate, searchCustomerId, searchInmateId, searchOrderId]);
  const selectedCase = overlapCases.find((c) => c.id === selectedCaseId);

  const overlapReduction =
    summary && summary.openFlags > 0 ? Math.round((1 - overlapCases.length / summary.openFlags) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Deposit Flag Review</h1>
          <p className="text-gray-600 mt-1">
            {summary?.openFlags?.toLocaleString() || 0} open flags. Start with shared cards and inmate overlap cases.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label htmlFor="flag-review-range" className="block text-xs font-medium text-gray-500 mb-1">
              Date range
            </label>
            <Flatpickr
              id="flag-review-range"
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-64"
              options={{ mode: 'range', dateFormat: 'Y-m-d', maxDate: 'today' }}
              value={[startDate, endDate]}
              onChange={(dates) => {
                if (dates.length === 2) {
                  setStartDate(fmtDate(dates[0]));
                  setEndDate(fmtDate(dates[1]));
                }
              }}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setStartDate(todayStr());
              setEndDate(todayStr());
            }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Today
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-6">{error}</div>
      ) : (
      <>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard label="Open flags" value={(summary?.openFlags || 0).toLocaleString()} icon={ShieldAlert} />
        <KpiCard label="Overlap cases" value={(overlapCases.length || 0).toLocaleString()} tone="info" icon={Layers} />
        <KpiCard label="Critical" value={(summary?.riskCounts?.critical || 0).toLocaleString()} tone="danger" icon={ShieldAlert} />
        <KpiCard
          label="Shared card"
          value={(summary?.scenarioCounts?.['Shared card'] || 0).toLocaleString()}
          tone="warning"
          icon={CreditCard}
        />
        <KpiCard label="Overlap savings" value={`${overlapReduction}%`} tone="success" icon={TrendingUp} />
      </div>

      {/* Recommended order callout */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <span className="font-semibold">Recommended review order:</span> 1) All shared-card flags · 2) Inmate overlap
        cases (many-customers + multi-state on same inmate — 2 flags, 1 review) · 3) Remaining by priority queue
      </div>

      {/* Trigger type + risk tier */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Open flags by trigger type</h2>
          {pie.series.length > 0 ? (
            <Chart
              type="donut"
              height={300}
              series={pie.series}
              options={{
                labels: pie.labels,
                colors: CHART_COLORS,
                legend: { position: 'bottom' },
                dataLabels: { enabled: true },
              }}
            />
          ) : (
            <p className="text-gray-500 text-center py-8">No data available</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Flags by risk tier</h2>
          <Chart
            type="bar"
            height={300}
            series={[{ name: 'Open flags', data: riskBar }]}
            options={{
              chart: { toolbar: { show: false } },
              colors: ['#3B82F6'],
              plotOptions: { bar: { borderRadius: 4, distributed: true, columnWidth: '45%' } },
              dataLabels: { enabled: true },
              legend: { show: false },
              xaxis: { categories: ['Critical', 'High', 'Medium'] },
            }}
          />
        </div>
      </div>

      {/* Top customer spread + inmate overlap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top customer spread (inmates per customer)</h2>
          {customerSpread.length > 0 ? (
            <Chart
              type="bar"
              height={320}
              series={[{ name: 'Inmate count', data: customerSpread.map((c) => c.inmateCount) }]}
              options={{
                chart: { toolbar: { show: false } },
                colors: ['#F59E0B'],
                plotOptions: { bar: { horizontal: true, borderRadius: 3 } },
                dataLabels: { enabled: true },
                xaxis: {
                  categories: customerSpread.map((c) => c.label),
                  title: { text: 'Inmates' },
                },
                yaxis: { title: { text: 'Customer' } },
                tooltip: { y: { formatter: (v) => `${v} inmate${v === 1 ? '' : 's'}` } },
              }}
            />
          ) : (
            <p className="text-gray-500 text-center py-8">No customer suspects linked to inmates</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Inmate overlap — flags per inmate</h2>
          {inmateOverlapChart.length > 0 ? (
            <Chart
              type="bar"
              height={320}
              series={[{ name: 'Flags', data: inmateOverlapChart.map((c) => c.flagCount) }]}
              options={{
                chart: { toolbar: { show: false } },
                colors: ['#EF4444'],
                plotOptions: { bar: { horizontal: true, borderRadius: 3 } },
                dataLabels: { enabled: true },
                xaxis: { categories: inmateOverlapChart.map((c) => c.label) },
                tooltip: { y: { formatter: (v) => `${v} flag${v === 1 ? '' : 's'}` } },
              }}
            />
          ) : (
            <p className="text-gray-500 text-center py-8">No many-customers-to-inmate flags</p>
          )}
        </div>
      </div>

      {/* Review queue */}
      <div className="bg-white rounded-lg shadow">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Review queue</h2>
          <div className="flex flex-wrap gap-2">
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="priority">Priority queue ({filteredQueue.length})</option>
              <option value="overlap">Inmate overlap ({overlapCases.length})</option>
              <option value="shared-card">Shared card ({sharedCardRows.length})</option>
              <option value="critical">Critical only ({criticalRows.length})</option>
            </select>
            {viewMode === 'priority' && (
              <select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              >
                <option value="all">All risks</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
              </select>
            )}
            {viewMode !== 'overlap' && (
              <>
                <input
                  type="text"
                  value={searchCustomerId}
                  onChange={(e) => setSearchCustomerId(e.target.value)}
                  placeholder="User ID"
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-32"
                />
                <input
                  type="text"
                  value={searchInmateId}
                  onChange={(e) => setSearchInmateId(e.target.value)}
                  placeholder="Inmate ID"
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-32"
                />
                <input
                  type="text"
                  value={searchOrderId}
                  onChange={(e) => setSearchOrderId(e.target.value)}
                  placeholder="Order ID"
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-32"
                />
                {(searchCustomerId || searchInmateId || searchOrderId) && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchCustomerId('');
                      setSearchInmateId('');
                      setSearchOrderId('');
                    }}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Clear
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="table-scroll">
          {viewMode === 'overlap' ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="px-4 py-2 font-medium">Case</th>
                  <th className="px-4 py-2 font-medium">Risk</th>
                  <th className="px-4 py-2 font-medium">Flags</th>
                  <th className="px-4 py-2 font-medium">Trigger mix</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {pagedOverlap.map((c) => (
                  <tr key={c.id} className={`border-b border-gray-100 ${RISK_ROW[c.risk] || ''}`}>
                    <td className="px-4 py-2 font-medium text-gray-900">{c.label}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RISK_BADGE[c.risk] || ''}`}>{c.risk}</span>
                    </td>
                    <td className="px-4 py-2">{c.flagCount}</td>
                    <td className="px-4 py-2 text-gray-600">
                      {c.scenarios.map((s) => SCENARIO_LABELS[s] || s).join(' + ')}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => setSelectedCaseId(c.id)}
                        className={`px-3 py-1 rounded text-xs ${
                          selectedCase?.id === c.id ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {overlapCases.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No overlap cases</td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="px-4 py-2 font-medium">Flag</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Detail</th>
                  <th className="px-4 py-2 font-medium">Risk</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {pagedFlags.map((f) => (
                  <tr key={f.id} className={`border-b border-gray-100 ${RISK_ROW[f.risk] || ''}`}>
                    <td className="px-4 py-2 font-mono font-medium text-gray-900">{f.id}</td>
                    <td className="px-4 py-2 text-gray-600">{SCENARIO_LABELS[f.flagType] || f.flagType || '—'}</td>
                    <td className="px-4 py-2 text-gray-600">{flagDetail(f)}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RISK_BADGE[f.risk] || ''}`}>{f.risk}</span>
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => setSelectedFlagId(f.id)}
                        className={`px-3 py-1 rounded text-xs ${
                          selectedFlag?.id === f.id ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {activeFlagRows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No flags in this view</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {currentRows.length > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-200 text-sm">
            <span className="text-gray-500">
              Showing {pageStart + 1}&ndash;{Math.min(pageStart + PAGE_SIZE, currentRows.length)} of {currentRows.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pageSafe <= 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-gray-600">Page {pageSafe} of {totalPages}</span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={pageSafe >= totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal: overlap case */}
      {viewMode === 'overlap' && selectedCase && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm"
          onClick={() => setSelectedCaseId(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl ring-1 ring-gray-200 max-w-2xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 truncate">{selectedCase.label}</h3>
                <p className="text-xs text-gray-500 mt-0.5">Review these flags together</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${RISK_BADGE[selectedCase.risk] || 'bg-gray-100 text-gray-700'}`}>
                  {selectedCase.risk}
                </span>
                <button
                  onClick={() => setSelectedCaseId(null)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-5 overflow-y-auto">
              <p className="text-sm text-gray-600 mb-5">
                This inmate has {selectedCase.flagCount} flags for different triggers. Review deposits once, then resolve
                every linked flag with the same note.
              </p>
              <div className="mb-5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Trigger mix</p>
                <div className="flex flex-wrap gap-2">
                  {selectedCase.scenarios.map((s) => (
                    <span key={s} className="px-2.5 py-1 rounded-md text-xs bg-gray-100 text-gray-700 border border-gray-200">
                      {SCENARIO_LABELS[s] || s}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
                  Linked flag IDs ({selectedCase.flagIds.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedCase.flagIds.map((fid) => (
                    <span key={fid} className="px-2.5 py-1 rounded-md bg-gray-50 border border-gray-200 text-gray-700 text-xs font-mono">{fid}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal: single flag */}
      {viewMode !== 'overlap' && selectedFlag && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm"
          onClick={() => setSelectedFlagId(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl ring-1 ring-gray-200 max-w-2xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 font-mono truncate">{selectedFlag.id}</h3>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${RISK_BADGE[selectedFlag.risk] || 'bg-gray-100 text-gray-700'}`}>
                  {selectedFlag.risk}
                </span>
              </div>
              <button
                onClick={() => setSelectedFlagId(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 overflow-y-auto">
              {selectedFlag.title && (
                <p className="text-sm text-gray-600 mb-4">{selectedFlag.title}</p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ['Inmates', selectedFlag.inmateCount],
                  ['Customers', selectedFlag.customerCount],
                  ['Orders', selectedFlag.orderCount],
                  ['Card last 4', selectedFlag.cardLast4 || '—'],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border border-gray-200 p-3">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
                    <p className="text-xl font-semibold text-gray-900 mt-1">{value}</p>
                  </div>
                ))}
              </div>

              {/* Actual ids involved in this flag */}
              <div className="mt-5 space-y-4">
                {[
                  ['Inmate APINs', selectedFlag.inmateIds],
                  ['Customer IDs', selectedFlag.customerIds],
                  ['User IDs', selectedFlag.userIds],
                  ['Order IDs', selectedFlag.orderIds],
                ].map(([label, ids]) => (
                  (ids && ids.length > 0) ? (
                    <div key={label}>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
                        {label} ({ids.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {ids.map((v) => (
                          <span key={`${label}-${v}`} className="px-2.5 py-1 rounded-md bg-gray-50 border border-gray-200 text-gray-700 text-xs font-mono">{v}</span>
                        ))}
                      </div>
                    </div>
                  ) : null
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200">
              <span className="text-xs text-gray-500 inline-flex items-center gap-1.5 min-w-0">
                <Users className="w-4 h-4 shrink-0" />
                <span className="truncate">{SCENARIO_LABELS[selectedFlag.flagType] || selectedFlag.flagType || '—'}</span>
              </span>
              {selectedFlag.dbId && (
                <Link to={`/flags/${selectedFlag.dbId}`} className="text-sm font-medium text-blue-600 hover:text-blue-700 shrink-0">
                  Open full flag details →
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default DepositFlagReview;
